class AccountDrawer extends HTMLElement {
  constructor() {
    super();

    this.externallyOpenedTier = null;
  }

  get selectors() {
    return {
      drawer: {
        button: {
          open: "[data-account-drawer-open]",
          close: "[data-account-drawer-close]"
        },
        scrollableContent: "[data-account-drawer-scrollable]"
      },
      tieredMenu: {
        container: "[data-account-drawer-tier]",
        trigger: "[data-account-drawer-tier-trigger]",
        backButton: "[data-account-drawer-tier-back-button]"
      }
    }
  }

  get modifiers() {
    return {
      drawer: {
        open: "drawer--open"
      },
      tieredMenu: {
        open: "account-drawer__navigation-tier--open",
        noTransition: "account-drawer__navigation-tier--no-transition"
      },
      button: {
        open: {
          pulse: "header__circle-icon--pulse"
        }
      }
    }
  }

  connectedCallback() {
    this.initTieredMenuListeners();
    this.initDrawerListeners();

    window.addEventListener("resize", this.setDrawerHeight.debounce(50).bind(this));
  }

  disconnectedCallback() {
    this.destroyTieredMenuListeners();
    this.destroyDrawerListeners();

    window.removeEventListener("resize", this.setDrawerHeight.debounce(50).bind(this));
  }

  initDrawerListeners() {
    document.documentElement.customDelegate(
      "click",
      this.selectors.drawer.button.open,
      this.openDrawer.bind(this)
    );

    document.documentElement.customDelegate(
      "click",
      this.selectors.drawer.button.close,
      this.closeDrawer.bind(this)
    );
  }

  destroyDrawerListeners() {
    document.documentElement.removeCustomDelegate(
      "click",
      this.selectors.drawer.button.open,
      this.openDrawer.bind(this)
    );

    document.documentElement.removeCustomDelegate(
      "click",
      this.selectors.drawer.button.close,
      this.closeDrawer.bind(this)
    );
  }

  openDrawer(event) {
    if (event) {
      event.preventDefault();
    }

    const scrollableContent = this.querySelector(this.selectors.drawer.scrollableContent);

    bodyScrollLock.disableBodyScroll(scrollableContent);

    this.classList.add(this.modifiers.drawer.open);

    this.focusDrawer();

    document.querySelector(this.selectors.drawer.button.open).classList.remove(this.modifiers.button.open.pulse);
  }

  closeDrawer(event) {
    if (event) {
      event.preventDefault();
    }

    const scrollableContent = this.querySelector(this.selectors.drawer.scrollableContent);

    this.classList.remove(this.modifiers.drawer.open);

    this.closeAllTieredMenus();

    bodyScrollLock.enableBodyScroll(scrollableContent);

    this.returnFocusToPreviousElement();

    this.externallyOpenedTier = null;
  }

  setDrawerHeight() {
    const drawerHeight = window.innerHeight;

    this.style.setProperty(
      '--drawer-height',
      `${drawerHeight}px`
    );
  }

  initTieredMenuListeners() {
    this._onTierTriggerKeydown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        const trigger = event?.target?.closest(this.selectors.tieredMenu.trigger);

        if (!trigger) {
          return false;
        }

        event.preventDefault();

        const menuId = trigger.getAttribute("data-account-drawer-tier-trigger");

        this.openTieredMenu(menuId);
      }
    };

    this._onTierTriggerClick = (event) => {
      const trigger = event?.target?.closest(this.selectors.tieredMenu.trigger);

      if (!trigger) {
        return;
      }

      event.preventDefault();

      const menuId = trigger.getAttribute("data-account-drawer-tier-trigger");

      this.openTieredMenu(menuId);
    };

    this._onTierBackButtonClick = (event) => {
      const backButton = event?.target?.closest(this.selectors.tieredMenu.backButton);

      if (!backButton) {
        return false;
      }

      event.preventDefault();

      this.closeTieredMenu(backButton);
    };

    document.addEventListener("keydown", this._onTierTriggerKeydown);
    document.addEventListener("click", this._onTierTriggerClick);
    document.addEventListener("click", this._onTierBackButtonClick);
  }

  destroyTieredMenuListeners() {
    document.removeEventListener("keydown", this._onTierTriggerKeydown);
    document.removeEventListener("click", this._onTierTriggerClick);
    document.removeEventListener("click", this._onTierBackButtonClick);
  }

  openTieredMenu(menuId, { immediate = false } = {}) {
    if (!menuId) {
      return;
    }

    const menu = document.querySelector(`[data-account-drawer-tier='${menuId}']`); // TODO

    if (!menu) {
      return;
    }

    if (immediate) {
      menu.classList.add(this.modifiers.tieredMenu.noTransition);
    }

    bodyScrollLock.disableBodyScroll(menu);

    menu.classList.add(this.modifiers.tieredMenu.open);

    if (!immediate) {
      const focusableElements = menu.getFocusableElements();
      const firstFocusableElement = focusableElements[0];

      setTimeout(() => {
        firstFocusableElement.focus();
      }, 400);

      menu.trapFocus();
    }

    if (immediate) {
      requestAnimationFrame(() => {
        menu.classList.remove(this.modifiers.tieredMenu.noTransition);
        menu.trapFocus();
      });
    }

    menu.removeAttribute("inert");
  }

  closeTieredMenu(backButton) {
    if (!backButton) {
      return;
    }

    const menu = backButton.closest(this.selectors.tieredMenu.container); // TODO

    if (!menu) {
      return;
    }

    menu.removeTrapFocus();

    menu.classList.remove(this.modifiers.tieredMenu.open);

    menu.setAttribute("aria-expanded", "false");

    if (this.externallyOpenedTier) {
      this.closeDrawer();
    }

    bodyScrollLock.enableBodyScroll(menu);
  }

  closeAllTieredMenus() {
    const tieredMenus = [
      ...this.querySelectorAll(this.selectors.tieredMenu.container)
    ];

    if (!tieredMenus.length) {
      return false;
    }

    tieredMenus.forEach((tieredMenu) => {
      tieredMenu.classList.remove(this.modifiers.tieredMenu.open);

      tieredMenu.setAttribute("aria-expanded", "false");

      bodyScrollLock.enableBodyScroll(tieredMenu);
    });
  }

  openDrawerWithTier(menuId) {
    this.externallyOpenedTier = menuId;

    this.openTieredMenu(menuId, { immediate: true });
    this.openDrawer();
  }

  focusDrawer() {
    this.previouslyFocusedElement = document.activeElement;

    this.focus({
      preventScroll: true
    });
  }

  returnFocusToPreviousElement() {
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }
}

if (!customElements.get("account-drawer")) {
  customElements.define("account-drawer", AccountDrawer);
}
